/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import React, { useState, useCallback, useContext, useRef, useMemo, useEffect } from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useMutation,
} from '@apollo/react-hooks';
import { css } from '@compiled/react';
import { useIntl, FormattedMessage, defineMessages } from 'react-intl-next';
import type { DataProxy } from 'apollo-cache';
import type { MutationUpdaterFn } from 'apollo-client';

import { ChromelessEditor } from '@atlaskit/editor-core/appearance-editor-chromeless';
import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import type { AnnotationInfo } from '@atlaskit/editor-plugin-annotation';
import { AnnotationUpdateEvent } from '@atlaskit/editor-common/types';
import Button from '@atlaskit/button/new';
import type { AnnotationUpdateEmitter } from '@atlaskit/editor-common/annotation';

import type { MutationFunctionInteropSupportedOptions } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import { commentsi18n } from '@confluence/comments-util/entry-points/commentsi18n.ts';
import { useUnreadCommentsState } from '@confluence/unread-comments/entry-points/useUnreadComments.ts';
import { CommentEditor } from '@confluence/comment/entry-points/LazyCommentEditor.ts';
import { NewReplyContainer } from '@confluence/inline-comments-common/entry-points/styled.ts';
import type { CommentLocation } from '@confluence/inline-comments-queries/entry-points/ActiveInlineCommentsQuery.ts';
import {
	useCommentsDataActions,
	CommentType,
	type CommentData,
	type ReplyData,
} from '@confluence/comments-data/entry-points/useCommentsData.ts';
import {
	useCommentsContentActions,
	useCommentsContentState,
} from '@confluence/comment-context/entry-points/useCommentsContent.ts';
import { ReactionsContext } from '@confluence/comment-context/entry-points/ReactionsContext.ts';
import { useAllCommentsData } from '@confluence/comments-data-manager/entry-points/useAllCommentsData.ts';
import { useCreateObjectCommentMutation } from '@confluence/comments-data-manager/entry-points/useCreateObjectCommentMutation.ts';
import { useCreateInlineReplyMutation } from '@confluence/comments-data-manager/entry-points/useCreateInlineReplyMutation.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import { useContentType } from '@confluence/page-context';
import {
	REPLY_TO_PAGE_COMMENT_LOAD_EXPERIENCE,
	REPLY_TO_INLINE_COMMENT_LOAD_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import type { PageInfoNode } from '@confluence/page-info/entry-points/PageInfoQuery.ts';
import type { CommentAction } from '@confluence/inline-comments-common/entry-points/inlineCommentsTypes.ts';
import {
	getValidDate,
	formatTimestampForAriaLabel,
} from '@confluence/comments-util/entry-points/dateUtils.ts';
import { fg } from '@confluence/feature-gating';
import { expVal, expValEquals } from '@confluence/feature-experiments';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs.ts';
import { CommentWarningDialog } from '@confluence/comment-dialogs';
import { WithCurvedBranchingStyle } from '@confluence/inline-comments-common/entry-points/components.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useSearchSessionId } from '@confluence/search-session/entry-points/SearchSessionState.ts';
import { RepliesToggler } from '@confluence/comments-common/entry-points/RepliesToggler.ts';
import { RepliesFooter } from '@confluence/comments-common/entry-points/RepliesFooter.ts';
import {
	ViewReplyOptions,
	getCommentReplies,
} from '@confluence/comments-common/entry-points/repliesHelpers.ts';
import { useUpdateVisibleReplies } from '@confluence/comments-common/entry-points/useUpdateVisibleReplies.ts';
import { GeneralReplyScroller } from '@confluence/comments-common/entry-points/GeneralReplyScroller.ts';
import { LocationValue } from '@confluence/comments-panel-utils/entry-points/commentsPanelHelper.ts';
import { useCommentsPanel } from '@confluence/comments-panel-utils/entry-points/useCommentsPanel.ts';
import { AnalyticsSource } from '@confluence/comments-util/entry-points/analytics.ts';
import { useAnnotationsIsOffline } from '@confluence/annotation-provider-store/entry-points/AnnotationsContext.ts';
import { useCommentService } from '@confluence/content-types-bridge-comments';

import {
	onSaveReply,
	getAdfForQuickReplyMention,
	getDefaultNumVisibleReplies,
	getDefaultReplyView,
	type SaveProps,
} from '../helper/commentThreadHelper.tsx';

import type {
	CommentRepliesCreateGeneralReplyMutation as CommentRepliesCreateGeneralReplyMutation$data,
	CommentRepliesCreateGeneralReplyMutationVariables as CommentRepliesCreateGeneralReplyMutation$variables,
} from './__types__/CommentRepliesCreateGeneralReplyMutation.ts';
import type {
	CommentRepliesCreateInlineReplyMutation as CommentRepliesCreateInlineReplyMutation$data,
	CommentRepliesCreateInlineReplyMutationVariables as CommentRepliesCreateInlineReplyMutation$variables,
} from './__types__/CommentRepliesCreateInlineReplyMutation.ts';
import { Comment } from './Comment.tsx';

const CommentRepliesCreateInlineReplyMutation = gql`
	mutation CommentRepliesCreateInlineReplyMutation(
		$input: ReplyInlineCommentInput!
		$pageId: ID!
		$includeReactions: Boolean!
	) {
		replyInlineComment(input: $input) {
			id
			body(representation: ATLAS_DOC_FORMAT) {
				value
			}
			contentStatus
			createdAt {
				value
			}
			createdAtNonLocalized
			links {
				webui
				editui
			}
			location {
				type
				... on InlineComment {
					inlineMarkerRef
					inlineText
					inlineResolveProperties {
						resolved
						isDangling
						resolvedFriendlyDate
						resolvedTime
						resolvedUser
					}
				}
				... on FooterComment {
					commentResolveProperties {
						resolved
						resolvedFriendlyDate
						resolvedTime
						resolvedUser
					}
				}
			}
			parentId
			permissions {
				isEditable
				isRemovable
				isResolvable
			}
			version {
				by {
					displayName
				}
				when
				number
			}
			author {
				displayName
				profilePicture {
					path
				}
				permissionType
				... on KnownUser {
					accountId
				}
			}
			createdAt {
				value
			}
			createdAtNonLocalized
			reactionsSummary(pageId: $pageId, contentType: "page", childType: "comment")
				@include(if: $includeReactions) {
				ari
				containerAri
				reactionsCount
				reactionsSummaryForEmoji {
					id
					emojiId
					count
					reacted
				}
			}
		}
	}
`;
const CommentRepliesCreateGeneralReplyMutation = gql`
	mutation CommentRepliesCreateGeneralReplyMutation(
		$pageId: ID!
		$cloudId: ID!
		$input: CreateCommentInput!
		$includeReactions: Boolean!
	) {
		createFooterComment(cloudId: $cloudId, input: $input) {
			id
			author {
				displayName
				profilePicture {
					path
				}
				permissionType
				... on KnownUser {
					accountId
				}
			}
			body(representation: ATLAS_DOC_FORMAT) {
				value
			}
			contentStatus
			createdAt {
				value
			}
			createdAtNonLocalized
			links {
				webui
				editui
			}
			location {
				type
				... on InlineComment {
					inlineMarkerRef
					inlineText
					inlineResolveProperties {
						isDangling
						resolvedTime
						resolvedFriendlyDate
						resolvedUser
						resolved
					}
				}
				... on FooterComment {
					commentResolveProperties {
						resolvedTime
						resolvedFriendlyDate
						resolvedUser
						resolved
					}
				}
			}
			parentId
			permissions {
				isEditable
				isRemovable
				isResolvable
			}
			reactionsSummary(pageId: $pageId, contentType: "page", childType: "comment")
				@include(if: $includeReactions) {
				ari
				containerAri
				reactionsCount
				reactionsSummaryForEmoji {
					id
					emojiId
					count
					reacted
				}
			}
			version {
				by {
					displayName
				}
				when
				number
			}
		}
	}
`;
const commentThreadRepliesBoxStyles = css({
	display: 'flex',
	flexDirection: 'column',
	paddingLeft: token('space.200'),
	paddingRight: token('space.100'),
});

const commentThreadRepliesBoxStylesV2 = css({
	paddingLeft: token('space.150'),
});

const commentThreadRepliesBoxStandaloneStyleOverrides = css({
	paddingRight: token('space.200'),
});

const commentThreadRepliesBoxStandaloneStyleOverridesV2 = css({
	paddingRight: token('space.150'),
});

const commentHandlesPaddingReplyBoxOverrides = css({
	paddingTop: token('space.0'),
	paddingLeft: token('space.0'),
	paddingBottom: token('space.0'),
});

const replyBoxStyles = css({
	listStyle: 'none',
	position: 'relative',
	paddingTop: token('space.100'),
	paddingBottom: token('space.100'),
	paddingLeft: token('space.300'),
});

const oldReplyBoxStyles = css({
	listStyle: 'none',
	position: 'relative',
	paddingTop: token('space.100'),
	paddingBottom: token('space.100'),
	paddingLeft: token('space.200'),
});

const translucentStyle = css({
	opacity: '50%',
});

const replyListStaticStraightBorders = css({
	paddingInlineStart: token('space.0'),
	borderLeft: `${token('border.width.selected')} solid ${token('color.border')}`,
});

const replyActionButtonStyles = {
	marginLeft: token('space.150'),
	width: '62px',
	height: '24px',
	left: '-12px',
	paddingTop: token('space.100'),
	paddingBottom: token('space.100'),
};

const customizedReplyButtonStyles = css({
	'&::after': {
		borderStyle: 'none',
	},
});

// This would cause the branching style to jump when clicking reply
const commentEditorLegacyCustomStyles = {
	marginLeft: token('space.300'),
	height: '16px',
	marginRight: token('space.050'),
};

const commentEditorCustomStyles = {
	marginLeft: token('space.300'),
	height: '24px', // 24px = 16px offset + 8px of padding
	marginRight: token('space.050'),
	paddingTop: token('space.100'),
};

export type CommentRepliesProps = {
	parentComment: CommentData;
	pageInfo: PageInfoNode | null;
	hoveredCommentId: string;
	pageMode: PageMode;
	editCommentQueryId?: string;
	selectedAnnotation: AnnotationInfo;
	parentCommentId: string;
	handleMouseEnter: (id?: string) => void;
	handleMouseLeave: () => void;
	canAddComments: boolean;
	isStandaloneComment?: boolean;
	deleteStandaloneComment?: (parentCommentId: string, commentId: string) => void;
	replyToComment?: (commentId: string, commentData: ReplyData) => void;
	updateReactionsCacheFn?: (
		emojiId: string,
		actionType: 'add' | 'delete',
		contentId: string,
		containerId?: string,
		cache?: DataProxy,
	) => MutationUpdaterFn<any> | void;
	onRendered?: () => void;
	eventEmitter?: AnnotationUpdateEmitter;
	isEditorExpanded?: boolean;
	hasParentMaxHeight?: boolean;
	flags?: FlagsStateContainer;
};

const i18n = defineMessages({
	replyAction: {
		id: 'comments-panel.reply.action',
		defaultMessage: 'Reply',
		description: 'Reply button label for comment thread in comments panel',
	},
	threadReplyButtonAriaLabelWithName: {
		id: 'comments-panel.reply.button.aria-label.with-name',
		defaultMessage: 'Reply to {name}’s thread',
		description: 'Reply placeholder for comment thread, with the first comment author name',
	},
	replyAriaLabel: {
		id: 'comments-panel.reply.aria-label',
		defaultMessage: 'Reply by {authorName}, {timestamp}',
		description: 'Aria label for reply container announcing the author and timestamp',
	},
});

export const CommentReplies = ({
	parentComment,
	pageInfo,
	hoveredCommentId,
	pageMode,
	editCommentQueryId,
	selectedAnnotation,
	parentCommentId,
	handleMouseEnter,
	handleMouseLeave,
	canAddComments,
	isStandaloneComment,
	deleteStandaloneComment,
	replyToComment,
	updateReactionsCacheFn,
	onRendered,
	eventEmitter,
	isEditorExpanded = false,
	hasParentMaxHeight = false,
	flags,
}: CommentRepliesProps) => {
	const [createInlineReplyFn] = useMutation<
		CommentRepliesCreateInlineReplyMutation$data,
		CommentRepliesCreateInlineReplyMutation$variables
	>(CommentRepliesCreateInlineReplyMutation);
	const [createGeneralReplyFn] = useMutation<
		CommentRepliesCreateGeneralReplyMutation$data,
		CommentRepliesCreateGeneralReplyMutation$variables
	>(CommentRepliesCreateGeneralReplyMutation);
	const { isReactionsEnabled } = useContext(ReactionsContext);

	const pageId = pageInfo?.id ?? '';
	const pageType = pageInfo?.type ?? '';

	const { createInlineReplyInterop } = useCreateInlineReplyMutation({ contentId: pageId });

	const { addCommentInCache } = useAllCommentsData({
		contentId: pageId,
		options: {
			skip: true, // Only using write operations, no need to fetch data
		},
	});
	const createObjectCommentMutationFn = useCreateObjectCommentMutation();

	const intl = useIntl();
	const { formatMessage } = intl;

	const { hasContentChanged } = useCommentsContentState();
	const { onChange, resetContentChanged } = useCommentsContentActions();

	const { cloudId } = useSessionData();
	const [{ searchSessionId }] = useSearchSessionId();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { addReplyToCommentThread } = useCommentsDataActions();
	const [{ replyToCommentId, location, replyToCommentUser }, { setReplyToCommentId }] =
		useCommentsPanel();
	const { unreadCommentIDSet } = useUnreadCommentsState();
	const isOffline = useAnnotationsIsOffline();
	const { commentService } = useCommentService();
	const [contentType] = useContentType();

	const allReplies = useMemo(
		() =>
			parentComment.replies.filter(
				(r) =>
					r !== null &&
					(parentComment.location as CommentLocation).inlineMarkerRef ===
						(r.location as CommentLocation).inlineMarkerRef,
			),
		[parentComment.replies, parentComment.location],
	);

	const numOfReplies = allReplies.length;

	const unreadReplyIds = useMemo(() => {
		return allReplies.filter((r) => unreadCommentIDSet.has(r.id)).map((r) => r.id);
	}, [allReplies, unreadCommentIDSet]);

	// Show the latest replies in the thread by default
	const [shouldShowLatestReplies, setShouldShowLatestReplies] = useState(true);
	const [visibleReplies, setVisibleReplies] = useState<ReplyData[] | undefined>(undefined);
	const [, setCommentForEdit] = useState('');
	const [commentIsFocused, setCommentIsFocused] = useState<string | null>(null);

	const { showModal } = useDialogs();

	const experienceTracker = useContext(ExperienceTrackerContext);

	const commentType = parentComment.type;
	const inlineCommentLocation = parentComment?.location as CommentLocation;
	const isDanglingComment =
		commentType === CommentType.INLINE &&
		inlineCommentLocation?.inlineResolveProperties?.isDangling &&
		!isStandaloneComment &&
		fg('confluence_frontend_new_dangling_comments_ux');
	const isParentCommentOpen = parentComment.isOpen;

	const isParentCommentResolvedOrDangling = !isParentCommentOpen || Boolean(isDanglingComment);

	const [currentReplyView, setReplyView] = useState(
		// Determine the reply view by counting the total number of top-level replies to the parent comment.
		// This excludes any nested replies.
		getDefaultReplyView(
			getCommentReplies(allReplies, parentCommentId).length,
			isParentCommentResolvedOrDangling,
			isStandaloneComment,
		),
	);

	// User page permissions
	const operations = pageInfo?.operations || [];

	const canAddComment = operations.some(
		(op) => op?.operation === 'create' && op?.targetType === 'comment',
	);

	const canAddAttachment = operations.some(
		(op) => op?.operation === 'create' && op?.targetType === 'attachment',
	);

	// The user can upload media only if they have update permissions for the page if confluence_comment_use_comment_media_endpoint FG is off
	const hasMediaUploadPermissions = fg('confluence_comment_use_comment_media_endpoint')
		? canAddComment && canAddAttachment
		: operations.some((op) => op?.operation === 'update' && op?.targetType === pageType);

	const threadKey =
		commentType === CommentType.INLINE
			? (parentComment?.location as CommentLocation).inlineMarkerRef || ''
			: parentCommentId;

	const supportedTopLevelActions: CommentAction[] = isParentCommentOpen
		? ['edit', 'resolve', 'delete']
		: ['reopen']; // reopen won't be rendered for reply, but we are using it to render disabled reaction summary for replies

	const expandReplyEditor = expValEquals('confluence_comments_tuneup', 'isEnabled', true)
		? isEditorExpanded
		: location === LocationValue.PANEL &&
			(replyToCommentId === parentCommentId ||
				(allReplies.some((reply) => reply.id === replyToCommentId) &&
					expValEquals('confluence_comment_expand_reply_editor', 'isEnabled', true)));

	const numOfHiddenReplies = useRef(
		currentReplyView === ViewReplyOptions.ALL ? 0 : numOfReplies - (visibleReplies?.length ?? 0),
	);

	// Track if we've already fired the analytics event for current visibleReplies state
	const hasFiredForVisibleRepliesRef = useRef<string>('');

	const topLevelReplyToNestedChildrenMap = useMemo(() => {
		const topLevelReplyIds = new Set(
			allReplies.filter((reply) => reply.parentId === parentCommentId).map((reply) => reply.id),
		);

		// We need to get all replies that are nested to any depth under a top level reply.
		// This will help us render the GeneralReplyScroller component for each reply (mainly, we need to show user icons and the blue dot indicator)
		const topLevelReplyToNestedChildrenMapInternal: {
			[key: string]: ReplyData[];
		} = Array.from(topLevelReplyIds).reduce<{
			[key: string]: ReplyData[];
		}>((acc, replyId) => {
			acc[replyId] = [];
			return acc;
		}, {});
		const replyIdToReplyMap = allReplies.reduce<{
			[key: string]: ReplyData;
		}>((acc, reply) => {
			acc[reply.id] = reply;
			return acc;
		}, {});

		// Map all replies to their top level parent. Replies can be nested to any depth, so we need to traverse the tree.
		const filteredReplies = allReplies.filter((reply) => !topLevelReplyIds.has(reply.id));

		for (const filteredReply of filteredReplies) {
			let replyParentId = filteredReply.parentId;

			// Add a circuit breaker to prevent infinite loops if we get really wonky data
			let circuitBreaker = 0;
			while (replyParentId && circuitBreaker < 100 && !topLevelReplyIds.has(replyParentId)) {
				replyParentId = replyIdToReplyMap[replyParentId]?.parentId;
				circuitBreaker++;
			}

			if (replyParentId && replyParentId in topLevelReplyToNestedChildrenMapInternal) {
				// Second condition will always be true, unless we somehow hit circuit breaker above
				topLevelReplyToNestedChildrenMapInternal[replyParentId].push(filteredReply);
			}
		}

		return topLevelReplyToNestedChildrenMapInternal;
	}, [allReplies, parentCommentId]);

	// Determine the latest top level replies to show on the thread's initial load
	const latestReplies = useMemo(() => {
		const replies: ReplyData[] = [];
		const topLevelReplies = allReplies.filter(
			(reply) => reply.id in topLevelReplyToNestedChildrenMap,
		);

		// Sort by createdAtNonLocalized value
		topLevelReplies.sort((a, b) => {
			// Safely handle missing or invalid dates
			const dateA = getValidDate(a?.createdAtNonLocalized);
			const dateB = getValidDate(b?.createdAtNonLocalized);

			return dateA.getTime() - dateB.getTime();
		});

		// Add the latest replies to the replies array
		for (
			let i = 0;
			i <
			getDefaultNumVisibleReplies(
				numOfReplies,
				isStandaloneComment,
				isParentCommentResolvedOrDangling,
			);
			i++
		) {
			if (topLevelReplies.length > 0) {
				const reply = topLevelReplies.pop();
				if (reply) {
					// Unshift to maintain reply chronological order
					replies.unshift(reply);
				}
			}
		}

		return replies;
	}, [
		allReplies,
		isStandaloneComment,
		numOfReplies,
		topLevelReplyToNestedChildrenMap,
		isParentCommentResolvedOrDangling,
	]);

	/*
	 * Determine the latest top level reply IDs and their nested reply IDs.
	 * This is passed to RepliesToggler to determine the hidden replies.
	 */
	const latestReplyIds = useMemo(() => {
		const latestAndNestedReplyIds: Set<string> = new Set();

		for (const reply of latestReplies) {
			latestAndNestedReplyIds.add(reply.id);

			for (const nestedReply of topLevelReplyToNestedChildrenMap[reply.id]) {
				latestAndNestedReplyIds.add(nestedReply.id);
			}
		}

		return latestAndNestedReplyIds;
	}, [latestReplies, topLevelReplyToNestedChildrenMap]);

	// Wrapper for setVisibleReplies to match the expected type signature
	const setVisibleRepliesWrapper = useCallback<React.Dispatch<React.SetStateAction<ReplyData[]>>>(
		(value) => {
			if (typeof value === 'function') {
				setVisibleReplies((prev) => {
					const prevArray = prev === undefined ? [] : prev;
					return value(prevArray);
				});
			} else {
				setVisibleReplies(value);
			}
		},
		[],
	);

	useUpdateVisibleReplies({
		allReplies,
		commentType,
		isParentCommentOpen,
		currentReplyView,
		latestReplies,
		latestReplyIds,
		parentCommentId,
		setVisibleReplies: setVisibleRepliesWrapper,
		shouldShowLatestReplies,
		numOfHiddenReplies,
	});

	// Track replies rendered state in experience tracker
	// Extract visibleReplies length to avoid complex expression in dependency array
	const visibleRepliesLength = visibleReplies?.length ?? -1;

	useEffect(() => {
		if (visibleReplies === undefined) {
			return;
		}

		const repliesRendered = currentReplyView === ViewReplyOptions.ALL && visibleReplies.length > 0;

		// Fire GASV3 event only when in ALL view with replies and we haven't fired for this state yet
		if (currentReplyView === ViewReplyOptions.ALL && visibleReplies.length > 0) {
			const stateKey = `${currentReplyView}-${visibleReplies.length}`;

			// Only fire if we haven't already fired for this exact state
			if (hasFiredForVisibleRepliesRef.current !== stateKey && numOfReplies !== 0) {
				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						action: 'repliesRenderedStateChanged',
						actionSubject: 'comment',
						actionSubjectId: parentCommentId,
						objectType: pageType,
						objectId: pageId,
						source: isStandaloneComment
							? AnalyticsSource.STANDALONE
							: AnalyticsSource.COMMENTS_PANEL,
						attributes: {
							commentType: commentType === CommentType.GENERAL ? 'page' : 'inline',
							repliesRendered,
							currentReplyView,
							numOfReplies,
							visibleRepliesCount: visibleReplies.length,
						},
					},
				}).fire();

				// Mark this state as fired
				hasFiredForVisibleRepliesRef.current = stateKey;
			}
		}
	}, [
		currentReplyView,
		visibleReplies,
		visibleRepliesLength,
		numOfReplies,
		commentType,
		experienceTracker,
		createAnalyticsEvent,
		parentCommentId,
		pageType,
		pageId,
		isStandaloneComment,
	]);

	const removeAnnotationHighlight = useCallback(() => {
		if (pageMode === PageMode.VIEW) {
			eventEmitter?.emit(AnnotationUpdateEvent.REMOVE_ANNOTATION_FOCUS);
		} else {
			eventEmitter?.emit('setselectedannotation', null);
		}
	}, [pageMode, eventEmitter]);

	// Reset ref on component unmount
	useEffect(() => {
		return () => {
			hasFiredForVisibleRepliesRef.current = '';
			//TODO: Remove once https://product-fabric.atlassian.net/browse/COMMENTS-5857 is resolved
			const isDatabaseOrWhiteboard = contentType === 'database' || contentType === 'whiteboard';
			if (
				!isDatabaseOrWhiteboard &&
				expValEquals('confluence_comment_reset_reply_options', 'isEnabled', true)
			) {
				setReplyToCommentId();
				removeAnnotationHighlight();
			}
		};
	}, [removeAnnotationHighlight, setReplyToCommentId, contentType]);

	const handleReplyButtonClick = () => {
		// Skip if there is no active inline comment annotation and currentlySelectedComment is not general
		const shouldRemoveAnnotationHighlight =
			commentType === CommentType.GENERAL &&
			selectedAnnotation.id &&
			fg('confluence_comment_panel_footer_reply_editor_focus');

		if (hasContentChanged) {
			// if the content has changed and the user has clicked reply button to another comment thread, warn the user
			showModal(CommentWarningDialog, {
				onConfirm: () => {
					resetContentChanged();
					setReplyToCommentId({ parentCommentId, location: LocationValue.PANEL });
					shouldRemoveAnnotationHighlight && eventEmitter && removeAnnotationHighlight();
				},
			});
		} else {
			setReplyToCommentId({ parentCommentId, location: LocationValue.PANEL });
			shouldRemoveAnnotationHighlight && eventEmitter && removeAnnotationHighlight();
		}
	};

	const handleFocus = useCallback(
		(event: React.FocusEvent, replyId: string) => {
			event.stopPropagation();
			setCommentIsFocused(replyId);
		},
		[setCommentIsFocused],
	);

	const handleBlur = useCallback(
		(event: React.FocusEvent) => {
			if (!event.relatedTarget || !event.currentTarget.contains(event.relatedTarget as Node)) {
				setCommentIsFocused(null);
			}
		},
		[setCommentIsFocused],
	);

	const renderReplyButton = () => {
		return (
			isParentCommentOpen &&
			canAddComments &&
			fg('confluence_frontend_comments_panel_v2') && (
				<WithCurvedBranchingStyle
					showDefaultStyles
					customStyles={replyActionButtonStyles}
					testId="comments-panel-reply-comment-action"
					onClick={handleReplyButtonClick}
				>
					<Button
						data-cy="comments-panel-reply-comment-action"
						css={customizedReplyButtonStyles}
						appearance="subtle"
						isDisabled={!isParentCommentOpen}
						analyticsContext={{
							attributes: {
								name: 'replyToCommentButton',
								source: AnalyticsSource.COMMENTS_PANEL,
							},
						}}
						interactionName="start-reply-comment-action"
						// TODO - COMMENTS-4934: Clean up confluence_frontend_comments_a11y_kb_exp FG
						{...(expVal('confluence_frontend_comments_a11y_kb_exp', 'isEnabled', false)
							? {
									'aria-label': formatMessage(i18n.threadReplyButtonAriaLabelWithName, {
										name:
											parentComment.author?.displayName || formatMessage(commentsi18n.anonymous),
									}),
								}
							: {})}
					>
						<FormattedMessage {...i18n.replyAction} />
					</Button>
				</WithCurvedBranchingStyle>
			)
		);
	};

	// Helper function to check if the reply editor is visible in the comments panel
	const isReplyEditorVisible = () => {
		// Use combined selector to find the specific reply editor for this comment thread
		const replyEditorContainer = document.querySelector(
			`[data-testid="comment-thread-${parentCommentId}-replies"] [data-testid="comments-panel-new-reply-container"]`,
		);
		const panelContainer = document.querySelector('[data-testid="comments-panel-list-container"]');

		if (!replyEditorContainer || !panelContainer) {
			return false;
		}

		const replyEditorRect = replyEditorContainer.getBoundingClientRect();
		const panelRect = panelContainer.getBoundingClientRect();

		// Check if the bottom of the reply editor container is visible within the panel
		return replyEditorRect.bottom <= panelRect.bottom;
	};

	const editorChangeHandler = () => {
		let experienceName = REPLY_TO_INLINE_COMMENT_LOAD_EXPERIENCE;

		if (commentType === CommentType.GENERAL) {
			experienceName = REPLY_TO_PAGE_COMMENT_LOAD_EXPERIENCE;
		}

		experienceTracker.succeed({
			name: experienceName,
		});

		// Check if we need to scroll to show the reply editor
		// Only scroll if the reply editor is for this specific comment thread
		if (
			replyToCommentId === parentCommentId &&
			!isReplyEditorVisible() &&
			expValEquals('confluence_comment_bring_replyeditor_intoview', 'isEnabled', true)
		) {
			// Use combined selector to find the specific reply editor for this comment thread
			const replyEditorContainer = document.querySelector(
				`[data-testid="comment-thread-${parentCommentId}-replies"] [data-testid="comments-panel-new-reply-container"]`,
			);
			if (replyEditorContainer) {
				replyEditorContainer.scrollIntoView({ behavior: 'smooth', block: 'end' });
			}
		}
	};

	// Wrap the createInlineReplyFn mutation with the interop logic that handles both the direct
	// Apollo mutation path and the new hook-based path for the Comments Data Manager
	const replyCreationInteropWrapper = useCallback(
		(
			options: MutationFunctionInteropSupportedOptions<
				CommentRepliesCreateInlineReplyMutation$data,
				CommentRepliesCreateInlineReplyMutation$variables
			>,
			onCacheUpdateError: (error: Error) => void,
		) =>
			createInlineReplyInterop<
				CommentRepliesCreateInlineReplyMutation$data,
				CommentRepliesCreateInlineReplyMutation$variables
			>({
				originalMutationFn: createInlineReplyFn,
				options,
				onCacheUpdateError,
			}),
		[createInlineReplyInterop, createInlineReplyFn],
	);

	const renderCommentEditor = () => {
		let customBranchingStyles = {};

		if (fg('confluence_frontend_comments_panel_v2')) {
			customBranchingStyles = commentEditorLegacyCustomStyles;
			if (fg('confluence_frontend_content_wrapper')) {
				// Accounts for extra padding in the author (was previously handled in a higher parent component)
				customBranchingStyles = commentEditorCustomStyles;
			}
		}

		const editor = (
			<CommentEditor
				pageId={pageId}
				pageType={pageType}
				content={
					fg('confluence_frontend_content_wrapper')
						? getAdfForQuickReplyMention(replyToCommentUser)
						: undefined
				}
				appearance="chromeless"
				EditorComponent={ChromelessEditor}
				onSaveComment={(adf, onSuccess) => {
					return onSaveReply({
						pageId,
						parentCommentId,
						cloudId,
						adf,
						onSuccess,
						pageMode,
						editCommentQueryId,
						setCommentForEdit,
						createAnalyticsEvent,
						pageType,
						experienceTracker,
						createInlineReplyFn: replyCreationInteropWrapper as SaveProps['createInlineReplyFn'],
						createGeneralReplyFn: createGeneralReplyFn as SaveProps['createGeneralReplyFn'],
						createObjectReplyFn: expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)
							? createObjectCommentMutationFn
							: undefined,
						addCommentInCommentsDataManagerCacheFn: expVal(
							'cc_comments_new_comments_data_manager',
							'isEnabled',
							false,
						)
							? addCommentInCache
							: undefined,
						setReplyToCommentId,
						resetContentChanged,
						setReplyView,
						selectedAnnotation,
						addReplyToCommentThread: expVal(
							'cc_comments_new_comments_data_manager',
							'isEnabled',
							false,
						)
							? () => {}
							: addReplyToCommentThread,
						threadKey,
						commentType,
						searchSessionId,
						isReactionsEnabled,
						analyticsSource: isStandaloneComment
							? AnalyticsSource.STANDALONE
							: AnalyticsSource.COMMENTS_PANEL,
						contentTypesCommentService: commentService,
					});
				}}
				onContentChange={onChange}
				onEditorReady={editorChangeHandler}
				commentMode="reply"
				commentType={commentType === CommentType.GENERAL ? 'page' : 'inline'}
				spaceId=""
				showCancelButton
				useNewWarningModal
				hideWatchCheckbox
				expandEditor={expandReplyEditor}
				pageMode="view"
				hasMediaUploadPermissions={hasMediaUploadPermissions}
				topLevelCommentId={parentComment.id}
				shouldWarnOnInternalNavigation
				onCancelComment={({ destroy = false }: { destroy?: boolean } = {}) => {
					// when the transition happens the
					// other reply list item is clicked
					// and we need to reset ssetReplyToCommentId
					if (!destroy || replyToCommentId !== parentCommentId) {
						setReplyToCommentId();
					}
				}}
				isCommentsPanel
				wasRemovedByAnotherUser={parentComment.wasRemovedByAnotherUser}
				isOffline={isOffline}
			/>
		);

		return (
			<NewReplyContainer
				mode="view"
				isCommentsPanel
				isFabricPage
				data-testid="comments-panel-new-reply-container"
			>
				{expValEquals('confluence_comments_tuneup', 'isEnabled', true) ? (
					editor
				) : (
					<WithCurvedBranchingStyle
						showDefaultStyles={fg('confluence_frontend_comments_panel_v2')}
						customStyles={customBranchingStyles}
					>
						{editor}
					</WithCurvedBranchingStyle>
				)}
			</NewReplyContainer>
		);
	};

	const renderUpdatedLowerAction = () => {
		if (expandReplyEditor && fg('confluence_frontend_comments_panel_v2')) {
			return renderCommentEditor();
		} else if (!isStandaloneComment) {
			return renderReplyButton();
		} else {
			return null;
		}
	};

	// Make reply containers tabbable for accessibility
	useEffect(() => {
		if (
			visibleReplies &&
			visibleReplies.length > 0 &&
			expVal('confluence_frontend_comments_a11y_kb_exp', 'isEnabled', false)
		) {
			// Find all reply containers and make them tabbable
			visibleReplies.forEach((reply) => {
				const replyContainer = document.querySelector(
					`[data-testid="comment-thread-${parentComment.id}-replies-${reply.id}"]`,
				);

				if (replyContainer instanceof HTMLElement) {
					replyContainer.setAttribute('tabIndex', '0');
				}
			});
		}
	}, [visibleReplies, parentComment.id]);

	const renderRepliesToggler = () => (
		<RepliesToggler
			replies={allReplies}
			setVisibleReplies={setVisibleRepliesWrapper}
			unreadReplyIds={unreadReplyIds}
			setReplyView={setReplyView}
			currentReplyView={currentReplyView}
			numOfHiddenReplies={numOfHiddenReplies}
			commentType={commentType}
			parentCommentId={parentCommentId}
			isParentOpen={isParentCommentOpen}
			canAddComments={canAddComments}
			shouldShowLatestReplies={shouldShowLatestReplies}
			setShouldShowLatestReplies={setShouldShowLatestReplies}
			latestReplyIds={latestReplyIds}
			isStandaloneComment={isStandaloneComment}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
			analyticsSource={
				isStandaloneComment ? AnalyticsSource.STANDALONE : AnalyticsSource.COMMENTS_PANEL
			}
			expandReplyEditor={expandReplyEditor}
		/>
	);

	return (
		<>
			{numOfReplies > 0 &&
				expValEquals('confluence_comments_tuneup', 'isEnabled', true) &&
				renderRepliesToggler()}
			<div
				data-testid={`comment-thread-${parentComment.id}-replies`}
				css={[
					commentThreadRepliesBoxStyles,
					expValEquals('confluence_comments_tuneup', 'isEnabled', true) &&
						commentThreadRepliesBoxStylesV2,
					parentComment.wasRemovedByAnotherUser && translucentStyle,
					isStandaloneComment && commentThreadRepliesBoxStandaloneStyleOverrides,
					isStandaloneComment &&
						expValEquals('confluence_comments_tuneup', 'isEnabled', true) &&
						commentThreadRepliesBoxStandaloneStyleOverridesV2,
				]}
			>
				<div>
					{numOfReplies > 0 &&
						!expValEquals('confluence_comments_tuneup', 'isEnabled', true) &&
						renderRepliesToggler()}
					{visibleReplies && visibleReplies.length > 0 && (
						<div
							css={[!fg('confluence_frontend_comments_panel_v2') && replyListStaticStraightBorders]}
							data-testid={`comment-thread-${parentComment.id}-reply-list`}
						>
							{visibleReplies.map((reply, idx) => {
								const isLastUnnestableReply = idx === visibleReplies.length - 1;

								return (
									// eslint-disable-next-line @atlassian/a11y/no-static-element-interactions, @atlassian/a11y/no-noninteractive-element-interactions
									<div
										css={[
											fg('confluence_frontend_comments_panel_v2')
												? replyBoxStyles
												: oldReplyBoxStyles,
											fg('confluence_frontend_content_wrapper') &&
												commentHandlesPaddingReplyBoxOverrides,
										]}
										key={reply.id}
										data-testid={`comment-thread-${parentComment.id}-replies-${reply.id}`}
										onMouseEnter={() => handleMouseEnter(reply.id)}
										onMouseLeave={handleMouseLeave}
										onFocus={(event) => handleFocus(event, reply.id)}
										onBlur={handleBlur}
										aria-label={formatMessage(i18n.replyAriaLabel, {
											authorName:
												reply.author?.displayName || formatMessage(commentsi18n.anonymous),
											timestamp: formatTimestampForAriaLabel({
												intl,
												dateString: reply.createdAtNonLocalized,
												isReply: true,
											}),
										})}
									>
										<Comment
											flags={flags}
											comment={reply}
											supportedTopLevelActions={supportedTopLevelActions}
											pageInfo={pageInfo}
											pageMode={pageMode}
											isUnread={unreadCommentIDSet.has(reply.id)}
											isHovered={
												hoveredCommentId === reply.id ||
												(commentIsFocused === reply.id &&
													expVal('confluence_frontend_comments_a11y_kb_exp', 'isEnabled', false))
											}
											threadKey={threadKey}
											// When the parent comment is resolved, hide the styles for the last reply
											hideBranchingStyle={
												(!canAddComments && isLastUnnestableReply) ||
												(isStandaloneComment && isLastUnnestableReply) ||
												(isLastUnnestableReply &&
													!expandReplyEditor &&
													expValEquals('confluence_comments_tuneup', 'isEnabled', true))
											}
											adjustForNestedReplies={
												commentType === CommentType.GENERAL &&
												reply.id in topLevelReplyToNestedChildrenMap &&
												topLevelReplyToNestedChildrenMap[reply.id]?.length > 0 &&
												fg('confluence_frontend_content_wrapper')
											}
											canAddComments={canAddComments}
											isParentCommentOpen={isParentCommentOpen}
											deleteStandaloneComment={deleteStandaloneComment}
											isStandaloneComment={isStandaloneComment}
											replyToComment={replyToComment}
											updateReactionsCacheFn={updateReactionsCacheFn}
											onRendered={onRendered}
											isLastUnnestableReply={isLastUnnestableReply}
											commentHandlesPadding={fg('confluence_frontend_content_wrapper')}
											hasParentMaxHeight={hasParentMaxHeight}
										/>
										{commentType === CommentType.GENERAL &&
											reply.id in topLevelReplyToNestedChildrenMap &&
											topLevelReplyToNestedChildrenMap[reply.id]?.length > 0 &&
											fg('confluence_frontend_content_wrapper') && (
												<GeneralReplyScroller
													nestedReplies={topLevelReplyToNestedChildrenMap[reply.id]}
													unreadReplyIds={unreadReplyIds}
													targetCommentIdForScroll={reply.id}
													isLastUnnestableReply={isLastUnnestableReply}
												/>
											)}
									</div>
								);
							})}
						</div>
					)}
				</div>
				{expValEquals('confluence_comments_tuneup', 'isEnabled', true) ? (
					<RepliesFooter
						replyView={currentReplyView}
						setReplyView={setReplyView}
						isEditorExpanded={isEditorExpanded}
						isParentCommentOpen={isParentCommentOpen}
						isStandaloneComment={isStandaloneComment}
						handleReplyButtonClick={handleReplyButtonClick}
						numOfVisibleReplies={visibleReplies ? visibleReplies.length : 0}
						isOffline={isOffline}
						scrollToId={threadKey}
						location={LocationValue.PANEL}
					>
						{renderCommentEditor()}
					</RepliesFooter>
				) : (
					renderUpdatedLowerAction()
				)}
			</div>
			{expandReplyEditor && !fg('confluence_frontend_comments_panel_v2') && renderCommentEditor()}
		</>
	);
};
